import { useState, useRef, useEffect, useCallback } from 'react'
import { Send, Square, MessageSquarePlus, Search, Loader2, BookOpen, FileText, Database, CheckCircle2, AlertCircle, Lightbulb, Brain, Shield, Star, X } from 'lucide-react'
import { ChatMessage } from './ChatMessage'
import { ArtifactPanel } from './ArtifactPanel'
import { useChat } from '../hooks/useChat'
import { useTranscripts } from '../hooks/useTranscripts'
import { useAuth } from '../lib/auth-context'
import { supabase } from '../lib/supabase'
import { logger } from '../lib/logger'
import type { Artifact, ContextPipelinePhase, Message, Conversation } from '../lib/types'

const pipelineIcons: Record<ContextPipelinePhase, typeof BookOpen> = {
  persona: BookOpen, transcripts: FileText, 'vector-db': Database,
  'tool-call': Search, complete: CheckCircle2, error: AlertCircle,
}

const pipelineLabels: Record<ContextPipelinePhase, string> = {
  persona: 'Loading', transcripts: 'Researching', 'vector-db': 'Processing',
  'tool-call': 'Searching', complete: 'Complete', error: 'Error',
}

function PipelineProgress({ progress }: { progress: { phase: ContextPipelinePhase; message: string; error?: string } }) {
  const Icon = pipelineIcons[progress.phase]
  return (
    <div className="mx-auto mb-4 animate-slide-up">
      <div className="card">
        <div className="flex items-center gap-3">
          {(['persona', 'transcripts', 'vector-db', 'tool-call'] as const).map((phase) => {
            const PhaseIcon = pipelineIcons[phase]
            const isActive = progress.phase === phase
            const isComplete = phase === 'persona' ? progress.phase !== 'persona'
              : phase === 'transcripts' ? ['vector-db', 'tool-call', 'complete'].includes(progress.phase)
              : phase === 'vector-db' ? ['tool-call', 'complete'].includes(progress.phase)
              : progress.phase === 'complete'
            return (
              <div key={phase} className="flex items-center gap-1.5">
                <div className={`avatar transition-all duration-300 ${
                  isActive ? 'bg-[hsl(var(--accent))] text-white scale-110' : isComplete ? 'bg-[hsl(var(--success)/0.2)] text-[hsl(var(--success))]' : 'bg-[hsl(var(--elevated))] text-[hsl(var(--fg-muted))]'
                }`}>
                  {isComplete ? <CheckCircle2 className="h-3.5 w-3.5" /> : <PhaseIcon className="h-3.5 w-3.5" />}
                </div>
                <span className={`text-caption ${isActive ? 'text-[hsl(var(--accent))] font-medium' : isComplete ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--fg-muted))]'}`}>
                  {pipelineLabels[phase]}
                </span>
                {phase !== 'tool-call' && <div className={`h-px w-5 ${isComplete ? 'bg-[hsl(var(--success)/0.5)]' : 'bg-[hsl(var(--border))]'}`} />}
              </div>
            )
          })}
        </div>
        <div className="mt-2.5 flex items-center gap-2 text-small text-[hsl(var(--fg-muted))]">
          {progress.phase !== 'error' && progress.phase !== 'complete' && (
            <div className="flex gap-1">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[hsl(var(--accent))] [animation-delay:-0.3s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[hsl(var(--accent))] [animation-delay:-0.15s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[hsl(var(--accent))]" />
            </div>
          )}
          <span className={progress.phase === 'error' ? 'text-[hsl(var(--error))]' : ''}>{progress.message}</span>
        </div>
      </div>
    </div>
  )
}

function TypingIndicator() {
  return (
    <div className="flex w-full justify-start mb-5 animate-slide-up">
      <div className="flex max-w-[85%] gap-3">
        <div className="avatar avatar-ai h-9 w-9">
          <BookOpen className="h-5 w-5" />
        </div>
        <div className="flex flex-col">
          <span className="text-caption font-medium text-[hsl(var(--fg-muted))] mb-1">Islam Preacher</span>
          <div className="msg-bubble px-5 py-3.5">
            <div className="flex gap-1.5">
              <span className="h-2 w-2 animate-bounce rounded-full bg-[hsl(var(--accent))] [animation-delay:-0.3s]" />
              <span className="h-2 w-2 animate-bounce rounded-full bg-[hsl(var(--accent))] [animation-delay:-0.15s]" />
              <span className="h-2 w-2 animate-bounce rounded-full bg-[hsl(var(--accent))]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function StreamingCursor({ content }: { content: string }) {
  if (!content) return null
  return (
    <span className="inline-block h-4 w-0.5 animate-pulse bg-[hsl(var(--accent))] ml-0.5 rounded-full" />
  )
}

interface ChatProps {
  model?: string
  apiKey?: string
  conversationId?: string
  mode?: 'learning' | 'debating'
  conversations?: Conversation[]
  userId?: string
  isGuest?: boolean
  onSignOut?: () => void
  onConversationCreated?: (id: string) => void
  onConversationUpdate?: (messages: Message[]) => void
}

export function Chat({ model, apiKey, conversationId, mode, conversations, userId, isGuest, onSignOut, onConversationCreated, onConversationUpdate }: ChatProps = {}) {
  const { user, profile, signOut } = useAuth()
  const {
    messages, isStreaming, error, pipelineProgress,
    sendMessage, regenerateLast, deleteMessage, stopGeneration, updateMessage,
    lastPipeline,
  } = useChat({
    model,
    apiKey,
    mode,
    conversations,
    userId,
    conversationId,
    onConversationCreated,
    userName: profile?.name || user?.user_metadata?.name || undefined,
    userReligion: profile?.religion || user?.user_metadata?.religion || undefined,
    userBelief: profile?.belief || undefined,
  })
  const [showSystemPrompt, setShowSystemPrompt] = useState(false)

  const { results: transcriptResults, loading: transcriptLoading, totalChunks, totalDocs, search: searchTranscripts, init: initTranscripts } = useTranscripts()

  const [input, setInput] = useState('')
  const [selectedArtifact, setSelectedArtifact] = useState<Artifact | null>(null)
  const [showTranscriptSearch, setShowTranscriptSearch] = useState(false)
  const [transcriptQuery, setTranscriptQuery] = useState('')
  const [showScrollBtn, setShowScrollBtn] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const isNearBottomRef = useRef(true)

  useEffect(() => { initTranscripts() }, [initTranscripts])

  // Persist messages to DB
  useEffect(() => {
    if (!conversationId || isStreaming || messages.length === 0) return
    const timer = setTimeout(() => {
      const toUpsert = messages.map(m => ({
        id: m.id,
        session_id: conversationId,
        role: m.role,
        content: m.content,
        thinking: m.thinking || null,
        artifacts: m.artifacts || [],
        pipeline_info: m.pipelineInfo || {},
        model: model || null,
        usage: m.usage || null,
      }))
      supabase.from('chat_messages').upsert(toUpsert).then(({ error }) => {
        if (error) logger.warn('Failed to persist messages', { error: error.message, conversationId })
      })
      const firstUser = messages.find(m => m.role === 'user')
      if (firstUser && messages.length <= 2) {
        const title = firstUser.content.slice(0, 60) + (firstUser.content.length > 60 ? '...' : '')
        supabase.from('chat_sessions').update({ title, updated_at: new Date().toISOString() }).eq('id', conversationId).then(({ error }) => {
          if (error) logger.warn('Failed to update session title', { error: error.message, conversationId })
        })
      }
      logger.debug('Messages persisted', { count: toUpsert.length, conversationId })
    }, 500)
    return () => clearTimeout(timer)
  }, [messages, conversationId, isStreaming, model])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setShowTranscriptSearch(p => !p) }
      if (e.key === 'Escape' && showTranscriptSearch) setShowTranscriptSearch(false)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showTranscriptSearch])

  // Auto-scroll
  useEffect(() => {
    if (isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, pipelineProgress])

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const threshold = 200
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold
    isNearBottomRef.current = nearBottom
    setShowScrollBtn(!nearBottom)
  }, [])

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    setShowScrollBtn(false)
    isNearBottomRef.current = true
  }, [])

  const handleInput = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const el = e.target
    el.style.height = '0'
    el.style.height = Math.min(el.scrollHeight, 160) + 'px'
  }, [])

  const handleSubmit = useCallback(() => {
    const trimmed = input.trim()
    if (!trimmed || isStreaming) return
    setInput('')
    sendMessage(trimmed)
  }, [input, isStreaming, sendMessage])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit() }
  }

  const handleTranscriptSearch = useCallback((query: string) => {
    setTranscriptQuery(query)
    searchTranscripts(query)
  }, [searchTranscripts])

  const welcomeTips = mode === 'learning' ? [
    { q: 'What are the five pillars of Islam?', icon: Lightbulb },
    { q: 'Who was Prophet Muhammad?', icon: BookOpen },
    { q: 'What does the Quran say about Jesus?', icon: Brain },
    { q: 'Why is charity important in Islam?', icon: Shield },
  ] : [
    { q: 'Why is Trinity logically impossible?', icon: Lightbulb },
    { q: 'How was the Quran preserved?', icon: BookOpen },
    { q: 'What does Islam say about science?', icon: Brain },
    { q: 'Prove Prophet Muhammad is a true prophet', icon: Shield },
  ]

  const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant')
  const isGenerating = isStreaming && lastAssistantMsg && !lastAssistantMsg.content

  return (
    <div className="flex h-full flex-col">
      <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-4 py-6 relative">
        {lastPipeline && (
          <button
            onClick={() => setShowSystemPrompt(true)}
            className="absolute top-4 right-4 z-10 rounded-lg bg-[hsl(var(--accent-soft))] px-3 py-1 text-caption text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent)/0.2)] border border-[hsl(var(--accent)/0.3)]"
            title="Show System Prompt"
          >
            Show Prompt
          </button>
        )}

        {showScrollBtn && (
          <button
            onClick={scrollToBottom}
            className="fixed bottom-28 left-1/2 -translate-x-1/2 z-20 rounded-full bg-[hsl(var(--accent))] text-white p-2.5 hover:bg-[hsl(var(--accent-hover))] animate-slide-up"
            title="Scroll to bottom"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12l7 7 7-7" />
            </svg>
          </button>
        )}

        <div className="container">
          {messages.length === 0 && !isStreaming && (
            <div className="flex flex-col items-center justify-center pt-16 text-center animate-fade-in">
              <div className="mb-8">
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-[hsl(var(--accent))]">
                  <MessageSquarePlus className="h-10 w-10 text-white" />
                </div>
              </div>
              <h1 className="text-h1 mb-2 font-bold text-[hsl(var(--accent))]">
                Islam Preacher
              </h1>
              <p className="text-body mb-3 max-w-md text-[hsl(var(--fg-muted))]">
                {mode === 'learning'
                  ? 'Ask anything about Islam — theology, history, scripture, or practice.'
                  : 'Challenge your beliefs in a structured theological discussion.'}
              </p>
              <p className="text-caption mb-6 text-[hsl(var(--fg-muted))]">
                Multi-faith knowledge engine — scripture from 5+ religions
              </p>

              <div className="mb-8 flex items-center justify-center gap-3 text-caption text-[hsl(var(--fg-muted))]">
                <span className="flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--elevated))] px-3 py-1.5">
                  <Search className="h-3 w-3" />
                  <span>Search all</span>
                  <kbd className="ml-0.5">
                    {navigator.platform?.includes('Mac') ? '⌘K' : 'Ctrl+K'}
                  </kbd>
                </span>
                <span className="flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--elevated))] px-3 py-1.5">
                  <Star className="h-3 w-3" />
                  Cross-reference
                </span>
              </div>

              <div className="grid w-full max-w-lg grid-cols-1 gap-3 sm:grid-cols-2">
                {welcomeTips.map(({ q, icon: Icon }) => (
                  <button
                    key={q}
                    onClick={() => { setInput(q); inputRef.current?.focus() }}
                    className="group relative flex items-center gap-3 card p-4 text-left transition-all duration-200 hover:border-[hsl(var(--accent)/0.5)] hover:bg-[hsl(var(--accent-soft))] active:scale-[0.98]"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--accent-soft))] text-[hsl(var(--accent))] group-hover:bg-[hsl(var(--accent)/0.2)] group-hover:scale-105 transition-all">
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className="text-body text-[hsl(var(--fg))] group-hover:text-[hsl(var(--accent))] transition-colors">{q}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map(msg => (
            <ChatMessage
              key={msg.id}
              role={msg.role}
              content={msg.content}
              thinking={msg.thinking}
              artifacts={msg.artifacts}
              pipelineInfo={msg.pipelineInfo}
              timestamp={msg.timestamp}
              usage={msg.usage}
              isStreaming={isStreaming && msg.id === lastAssistantMsg?.id}
              onArtifactSelect={setSelectedArtifact}
              onDelete={() => deleteMessage(msg.id)}
            />
          ))}

          {/* Typing indicator when AI is generating but has no content yet */}
          {isGenerating && <TypingIndicator />}

          {/* Pipeline progress during initial processing */}
          {pipelineProgress && pipelineProgress.phase !== 'complete' && pipelineProgress.phase !== 'error' && (
            <PipelineProgress progress={pipelineProgress} />
          )}

          {/* Guest banner */}
          {isGuest && messages.length >= 2 && !isStreaming && (
            <div className="mx-auto mb-4 animate-slide-up">
              <div className="card border-[hsl(var(--accent)/0.3)] bg-[hsl(var(--accent-soft))] p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--accent))]/20">
                    <BookOpen className="h-4 w-4 text-[hsl(var(--accent))]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-small font-medium text-[hsl(var(--fg))]">You're browsing as a guest</p>
                    <p className="text-caption text-[hsl(var(--fg-muted))]">Create an account to save your conversations and access all features.</p>
                  </div>
                  <button
                    onClick={() => window.location.href = '/'}
                    className="btn-cta text-small whitespace-nowrap shrink-0"
                  >
                    Sign Up Free
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="mx-auto mb-4 animate-slide-up">
              <div className="card border-[hsl(var(--error)/0.3)] bg-[hsl(var(--error-bg))] p-3 flex items-center gap-2 text-body text-[hsl(var(--error))]">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
                <button onClick={() => regenerateLast()} className="ml-auto btn btn-sm btn-destructive">
                  Retry
                </button>
              </div>
            </div>
          )}

          {!apiKey && (
            <div className="mx-auto mb-4 card border-[hsl(var(--accent)/0.3)] bg-[hsl(var(--accent-soft))] p-3 flex items-center gap-2 text-body text-[hsl(var(--accent))]">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>No OpenRouter API key set.</span>
            </div>
          )}

          {messages.length >= 2 && !isStreaming && !error && messages[messages.length - 1]?.role === 'assistant' && (
            <div className="flex justify-center mt-4 animate-fade-in">
              <button
                onClick={regenerateLast}
                className="flex items-center gap-2 rounded-xl border bg-[hsl(var(--surface))] px-4 py-2 text-small text-[hsl(var(--fg-muted))] hover:border-[hsl(var(--accent)/0.5)] hover:text-[hsl(var(--accent))] active:scale-95"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" />
                  <polyline points="1 20 1 14 7 14" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
                Regenerate
              </button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="border-t border-[hsl(var(--border))] bg-[hsl(var(--surface))]/95 backdrop-blur-sm px-4 py-3">
        <div className="container">
          <div className="flex items-end gap-2">
            <div className="relative flex-1">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                placeholder={isStreaming ? 'Waiting for response...' : 'Ask a question about Islam...'}
                rows={1}
                maxLength={2500}
                disabled={isStreaming}
                className="chat-input-area min-h-[48px] w-full resize-none input-field rounded-xl pr-24 pl-4 py-3 disabled:opacity-50 text-body leading-relaxed"
              />
              <div className="absolute bottom-2 right-2 flex items-center gap-0.5">
                <button
                  onClick={() => setShowTranscriptSearch(!showTranscriptSearch)}
                  className="btn btn-ghost btn-icon hover:text-[hsl(var(--accent))]"
                  title="Search transcripts"
                >
                  <Search className="h-4 w-4" />
                </button>
              </div>
            </div>
            {isStreaming ? (
              <button
                onClick={stopGeneration}
                className="flex h-[48px] w-[48px] items-center justify-center rounded-xl bg-[hsl(var(--error))] text-white hover:bg-[hsl(var(--error)/0.8)] active:scale-90 transition-all"
                title="Stop generating"
              >
                <Square className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!input.trim() || !apiKey}
                className="flex h-[48px] w-[48px] items-center justify-center rounded-xl bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent-hover))] disabled:opacity-40 disabled:cursor-not-allowed active:scale-90 transition-all shadow-lg shadow-[hsl(var(--accent)/0.2)]"
                title="Send message"
              >
                <Send className="h-4 w-4" />
              </button>
            )}
          </div>
          {isStreaming && (
            <div className="mt-2 flex items-center gap-2 px-1">
              <div className="flex gap-0.5">
                <span className="h-1 w-1 animate-pulse rounded-full bg-[hsl(var(--accent))]" />
                <span className="h-1 w-1 animate-pulse rounded-full bg-[hsl(var(--accent))] [animation-delay:0.15s]" />
                <span className="h-1 w-1 animate-pulse rounded-full bg-[hsl(var(--accent))] [animation-delay:0.3s]" />
              </div>
              <span className="text-caption text-[hsl(var(--fg-muted))]">
                {pipelineProgress?.phase === 'tool-call' ? 'Searching references...' : 'Generating response...'}
              </span>
            </div>
          )}
          {!isStreaming && !apiKey && (
            <div className="mt-2 flex items-center gap-2 px-1">
              <span className="text-caption text-[hsl(var(--error))]">
                No API key set. <button onClick={() => {}} className="underline hover:text-[hsl(var(--accent))]">Open settings</button>
              </span>
            </div>
          )}
        </div>
      </div>

      {showTranscriptSearch && (
        <div className="absolute inset-x-0 bottom-0 top-16 z-40 mx-auto overflow-auto card animate-slide-up">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-h4 text-[hsl(var(--fg))]">Search Transcripts</h3>
            <button onClick={() => setShowTranscriptSearch(false)} className="btn btn-ghost btn-icon"><X className="h-4 w-4" /></button>
          </div>
          <input
            type="text"
            value={transcriptQuery}
            onChange={e => handleTranscriptSearch(e.target.value)}
            placeholder="Search transcripts..."
            className="input-field"
            autoFocus
          />
          <div className="mt-3 max-h-80 overflow-y-auto rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--bg))]">
            {transcriptLoading && <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>}
            {!transcriptLoading && transcriptQuery && transcriptResults.length === 0 && <p className="py-8 text-center text-body text-[hsl(var(--fg-muted))]">No matches</p>}
            {transcriptResults.map(({ chunk, score }) => (
              <button
                key={chunk.id}
                onClick={() => {
                  setInput(prev => prev + `\n[From "${chunk.title || chunk.source}": ${chunk.preview.slice(0, 120)}...]`)
                  setShowTranscriptSearch(false)
                  inputRef.current?.focus()
                }}
                className="w-full border-b border-[hsl(var(--border))] last:border-0 px-4 py-3 text-left hover:bg-[hsl(var(--accent-soft))] transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium text-[hsl(var(--fg))]">{chunk.title || chunk.source}</span>
                  <span className="text-small text-[hsl(var(--accent))]">{score}</span>
                </div>
                <p className="mt-1 text-small text-[hsl(var(--fg-muted))] line-clamp-2">{chunk.preview}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <ArtifactPanel artifact={selectedArtifact} onClose={() => setSelectedArtifact(null)} />

      {showSystemPrompt && (
        <div className="modal-overlay" onClick={() => setShowSystemPrompt(false)}>
          <div className="modal-panel modal-panel-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-h4 text-[hsl(var(--fg))]">System Prompt</h2>
              <button onClick={() => setShowSystemPrompt(false)} className="btn btn-ghost btn-icon"><X className="h-5 w-5" /></button>
            </div>
            <pre className="whitespace-pre-wrap text-body text-[hsl(var(--fg))] p-4 bg-[hsl(var(--elevated))] rounded-lg overflow-auto max-h-[70vh] font-mono">
              {lastPipeline?.combinedSystemPrompt || 'No system prompt available'}
            </pre>
          </div>
        </div>
      )}
    </div>
  )
}
